import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Envelope, Phone, CheckCircle, Handshake, Certificate, Users, WhatsappLogo } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '6285921774979';

export default function About() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const features = [
    { icon: Handshake, title: t('about.features.partner'), desc: t('about.features.partnerDesc') },
    { icon: Certificate, title: t('about.features.support'), desc: t('about.features.supportDesc') },
    { icon: Users, title: t('about.features.consultation'), desc: t('about.features.consultationDesc') },
  ];

  useEffect(() => {
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('about.contact.form.success'));
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error(t('about.contact.form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const whyPoints = t('about.whyPoints');

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Header */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('about.subtitle')}</p>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="about-title">
                {t('about.title')}
              </h1>
              <p className="text-xgrids-text-secondary text-lg leading-relaxed mb-6">
                {t('about.desc1')}
              </p>
              <p className="text-xgrids-text-secondary leading-relaxed">
                {t('about.desc2')}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square bg-xgrids-bg-surface rounded-sm overflow-hidden border border-white/10">
                <img
                  src="https://xgrids.com/assets/logo_white.ceffc551.png"
                  alt="XGRIDS"
                  className="w-full h-full object-contain p-16"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-xgrids-brand-primary text-xgrids-bg-primary px-6 py-3">
                <p className="font-heading font-bold text-lg">{t('about.officialPartner')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-hover bg-xgrids-bg-surface p-8 rounded-sm"
                data-testid={`feature-${index}`}
              >
                <feature.icon size={40} weight="duotone" className="text-xgrids-brand-primary mb-6" />
                <h3 className="font-heading text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-xgrids-text-secondary">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why XGRIDS */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('about.whyXgrids')}</p>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-8">
                {t('about.whyXgridsTitle')}
              </h2>
              <ul className="space-y-6">
                {Array.isArray(whyPoints) && whyPoints.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <CheckCircle size={24} weight="fill" className="text-xgrids-brand-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xgrids-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-xgrids-bg-surface p-8 rounded-sm text-center">
                <p className="font-heading text-4xl font-bold text-xgrids-brand-primary">2000+</p>
                <p className="text-xgrids-text-secondary text-sm mt-2">{t('about.stats.customers')}</p>
              </div>
              <div className="bg-xgrids-bg-surface p-8 rounded-sm text-center">
                <p className="font-heading text-4xl font-bold text-xgrids-brand-blue">50+</p>
                <p className="text-xgrids-text-secondary text-sm mt-2">{t('about.stats.patents')}</p>
              </div>
              <div className="bg-xgrids-bg-surface p-8 rounded-sm text-center">
                <p className="font-heading text-4xl font-bold text-purple-400">80%</p>
                <p className="text-xgrids-text-secondary text-sm mt-2">{t('about.stats.rdTeam')}</p>
              </div>
              <div className="bg-xgrids-bg-surface p-8 rounded-sm text-center">
                <p className="font-heading text-4xl font-bold text-amber-400">±2mm</p>
                <p className="text-xgrids-text-secondary text-sm mt-2">{t('about.stats.accuracy')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-28 scroll-mt-24" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('about.contact.title')}</p>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
                {t('about.contact.subtitle')}
              </h2>
              <p className="text-xgrids-text-secondary mb-10">
                {t('about.contact.desc')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-xgrids-bg-surface border border-white/10 rounded-sm">
                    <MapPin size={24} className="text-xgrids-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white mb-1">{t('about.contact.officeAddress')}</h4>
                    <p className="text-xgrids-text-secondary">
                      Perkantoran Multiguna Bintaro<br />
                      Sektor 3A No 8M<br />
                      Tangerang Selatan, Indonesia
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-xgrids-bg-surface border border-white/10 rounded-sm">
                    <Envelope size={24} className="text-xgrids-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white mb-1">{t('about.contact.email')}</h4>
                    <a href="mailto:info@xgrids.id" className="text-xgrids-text-secondary hover:text-xgrids-brand-primary transition-colors">
                      info@xgrids.id
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-xgrids-bg-surface border border-white/10 rounded-sm">
                    <Phone size={24} className="text-xgrids-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white mb-1">{t('about.contact.phone')}</h4>
                    <div className="flex flex-col gap-1">
                      <a href="tel:+6285921774979" className="text-xgrids-text-secondary hover:text-xgrids-brand-primary transition-colors">
                        +62 859-2177-4979
                      </a>
                      <a href="tel:+628122033957" className="text-xgrids-text-secondary hover:text-xgrids-brand-primary transition-colors">
                        +62 812-2033-957
                      </a>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Button */}
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(language === 'en' ? 'Hello XGRIDS Indonesia, I am interested in your products.' : 'Halo XGRIDS Indonesia, saya tertarik dengan produk Anda.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-sm transition-colors mt-4"
                  data-testid="whatsapp-contact-btn"
                >
                  <WhatsappLogo size={24} weight="fill" />
                  <span className="font-medium">Chat via WhatsApp</span>
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="bg-xgrids-bg-surface p-8 rounded-sm border border-white/10" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-xgrids-text-secondary mb-2">{t('about.contact.form.name')} *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-xgrids-text-secondary mb-2">{t('about.contact.form.email')} *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-xgrids-text-secondary mb-2">{t('about.contact.form.phone')}</label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+62 812 3456 7890"
                      className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-xgrids-text-secondary mb-2">{t('about.contact.form.company')}</label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="PT Example Indonesia"
                      className="bg-xgrids-bg-primary border-white/10 text-white rounded-none"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-xgrids-text-secondary mb-2">{t('about.contact.form.message')} *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder={t('about.contact.form.messagePlaceholder')}
                    rows={5}
                    className="bg-xgrids-bg-primary border-white/10 text-white rounded-none resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-xgrids-brand-primary text-xgrids-bg-primary hover:bg-xgrids-brand-hover rounded-none py-6 font-semibold"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? t('about.contact.form.sending') : t('about.contact.form.submit')}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
